export interface FileProcessResult {
  text: string;
  fileName: string;
  fileType: string;
}

/**
 * Extract text from PDF file using PDF.js
 */
async function extractPDFText(file: File): Promise<string> {
  try {
    // Dynamic import of PDF.js for better compatibility
    const pdfjsLib = await import('pdfjs-dist');
    
    // Configure PDF.js worker for older version
    pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;
    
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;
    
    let fullText = '';
    
    // Extract text from each page
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      
      // Combine text items with spaces
      const pageText = textContent.items
        .map((item: any) => item.str)
        .join(' ');
      
      fullText += pageText + '\n\n';
    }
    
    return fullText.trim();
  } catch (error) {
    console.error('Error extracting PDF text:', error);
    throw new Error('Failed to extract text from PDF. The file might be corrupted, password-protected, or contains only images.');
  }
}

/**
 * Process file and extract text content
 */
export async function processFile(file: File): Promise<FileProcessResult> {
  const fileName = file.name;
  const extension = fileName.toLowerCase().split('.').pop();
  
  let text: string;
  let fileType: string;
  
  try {
    switch (extension) {
      case 'txt':
        text = await file.text();
        fileType = 'text';
        break;
        
      case 'md':
        text = await file.text();
        fileType = 'markdown';
        break;
        
      case 'pdf':
        text = await extractPDFText(file);
        fileType = 'pdf';
        break;
        
      default:
        throw new Error(`Unsupported file type: .${extension}`);
    }
    
    if (!text.trim()) {
      throw new Error('The file appears to be empty or contains no readable text.');
    }
    
    return {
      text: text.trim(),
      fileName,
      fileType
    };
    
  } catch (error) {
    console.error('File processing error:', error);
    throw error;
  }
}

/**
 * Check if file type is supported
 */
export function isSupportedFileType(fileName: string): boolean {
  const extension = fileName.toLowerCase().split('.').pop();
  return ['txt', 'md', 'pdf'].includes(extension || '');
}

/**
 * Get supported file extensions for file input
 */
export function getSupportedFileExtensions(): string {
  return '.txt,.md,.pdf';
}

/**
 * Get file size limit (10MB)
 */
export function getMaxFileSize(): number {
  return 10 * 1024 * 1024; // 10MB in bytes
} 